import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, Globe, Plus, Building2, Settings2, UserCog, ScrollText, PackageSearch, CalendarClock, Boxes, FileText, CreditCard, Rocket, MapPin, LayoutDashboard, Users, UserRound } from "lucide-react";
import { AdminOps } from "@/components/AdminOps";
import { AdminFinance } from "@/components/AdminFinance";
import { AdminTariffs } from "@/components/AdminTariffs";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { AdminHubs } from "@/components/AdminHubs";
import { AdminCatalog } from "@/components/AdminCatalog";
import { AdminLayout } from "@/components/AdminLayout";
import { AdminDepartures } from "@/components/AdminDepartures";
import { AdminInvoices } from "@/components/AdminInvoices";
import { CapabilitiesDialog } from "@/components/AdminGovernance";
import { DisputesPanel, ClientsPanel, GeoPanel, ChevronHint } from "@/components/AdminPanels";
import { ProhibitedEditor } from "@/components/ProhibitedGoods";

const PERMISSION_LABELS = {
  customers: "Clients", partners: "Partenaires", geo: "Pays & villes", routes: "Routes",
  rates: "Tarifs", shipments: "Expéditions", quotes: "Devis", reviews: "Avis",
  disputes: "Litiges", reports: "Rapports & paiements", features: "Fonctionnalités",
  shopship: "Achats en ligne", hubs: "Hubs Colis2Go", catalog: "Catalogue & Tarifs",
};

const FEATURE_LABELS = {
  air: "Groupage aérien", sea: "Groupage maritime", quotes: "Demande de devis",
  reviews: "Avis clients", partner_registration: "Inscription partenaires",
  customer_registration: "Inscription clients", consolidation: "Consolidation multi-colis",
  qr: "QR codes", tracking: "Suivi des expéditions", messaging: "Messagerie interne",
  disputes: "Litiges", payments: "Paiements", shop_and_ship: "Achats en ligne",
};

const STATUS_BADGE = {
  approved: ["bg-emerald-600 text-white", "Approuvé"],
  pending: ["bg-amber-100 text-amber-700", "En attente"],
  suspended: ["bg-red-100 text-red-700", "Suspendu"],
  refused: ["bg-muted text-muted-foreground", "Refusé"],
};

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { refresh: refreshFeatures } = useFeatures();
  const isSuper = user?.role === "super_admin";
  const has = (perm) => isSuper || (user?.permissions || []).includes(perm);

  const [tab, setTab] = useState("dashboard");
  const [hub, setHub] = useState("all"); // GLOBAL | HUB UK | HUB FRANCE
  const [src, setSrc] = useState("all"); // TOUT | ACHATS EN LIGNE | MARCHANDISE
  const [stats, setStats] = useState(null);
  const [opsStats, setOpsStats] = useState(null);
  const [deps, setDeps] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [geo, setGeo] = useState({ countries: [], cities: [] });
  const [routes, setRoutes] = useState([]);
  const [features, setFeatures] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [ssPurchases, setSsPurchases] = useState([]);
  const [filters, setFilters] = useState({});
  const setFilter = (t, f) => setFilters((x) => ({ ...x, [t]: f }));

  const load = () => {
    api.get(`/admin/stats?hub=${hub}&src=${src}`).then(({ data }) => setStats(data));
    if (has("shopship")) api.get(`/admin/ops/stats?hub=${hub}&src=${src}`).then(({ data }) => setOpsStats(data)).catch(() => {});
    if (has("shopship")) api.get(`/admin/departures?hub=${hub}`).then(({ data }) => setDeps(data)).catch(() => {});
    if (has("partners")) api.get("/admin/partners").then(({ data }) => setPartners(data));
    if (has("customers")) api.get("/admin/users").then(({ data }) => setUsers(data));
    if (has("shipments")) api.get(`/admin/shipments?hub=${hub}&src=${src}`).then(({ data }) => setShipments(data));
    if (has("geo")) api.get("/admin/geo").then(({ data }) => setGeo(data));
    if (has("routes")) api.get("/admin/routes").then(({ data }) => setRoutes(data));
    if (has("features")) api.get("/admin/features").then(({ data }) => setFeatures(data));
    if (isSuper) api.get("/admin/admins").then(({ data }) => setAdmins(data));
    if (has("reports")) api.get("/admin/audit").then(({ data }) => setAudit(data));
    if (has("disputes")) api.get("/admin/disputes").then(({ data }) => setDisputes(data)).catch(() => {});
    if (has("shopship")) api.get("/admin/shopship/purchases").then(({ data }) => setSsPurchases(data)).catch(() => {});
  };
  useEffect(load, [hub, src]); // eslint-disable-line

  const verify = async (p, val) => { await api.post(`/admin/partners/${p.id}/verify`, { verified: val }); toast.success(val ? "Partenaire vérifié" : "Vérification retirée"); load(); };
  const setLevel = async (p, level) => { await api.post(`/admin/partners/${p.id}/verify`, { verified: p.verified, level }); toast.success("Niveau mis à jour"); load(); };
  const setStatus = async (p, status) => { await api.post(`/admin/partners/${p.id}/status`, { status }); toast.success("Statut mis à jour"); load(); };
  const [capPartner, setCapPartner] = useState(null);
  const updateCountry = async (c, patch) => { await api.put(`/admin/countries/${c.id}`, patch); load(); };
  const updateCity = async (c, patch) => { await api.put(`/admin/cities/${c.id}`, patch); load(); };
  const toggleFeature = async (key, val) => {
    const { data } = await api.put("/admin/features", { flags: { [key]: val } });
    setFeatures(data); refreshFeatures();
    toast.success(`${FEATURE_LABELS[key]} ${val ? "activé" : "désactivé"}`);
  };

  // TABLEAU DE BORD : « Qu'est-ce qu'on doit faire aujourd'hui ? » — 6 compteurs opérationnels
  const cards = opsStats ? [
    { icon: PackageSearch, label: "À recevoir", value: opsStats.expected, filter: "expected" },
    { icon: Building2, label: "Au Hub", value: opsStats.received, filter: "received" },
    { icon: FileText, label: "À facturer / À payer", value: (opsStats.to_price || 0) + (opsStats.to_price_online || 0) + (opsStats.payments || 0), filter: "to_price" },
    { icon: Rocket, label: "Prêts au départ", value: opsStats.ready, filter: "ready" },
    { icon: Truck, label: "En transit", value: opsStats.transit, filter: "transit" },
    { icon: MapPin, label: "Arrivés / À remettre", value: opsStats.arrived, filter: "arrived" },
  ] : [];
  const openCard = (c) => { if (!has("shopship")) return; setFilter("ops", c.filter); setTab("ops"); };

  // MENU PRINCIPAL SUPER ADMIN — sidebar sombre (image de référence)
  const MENU = [
    { key: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, show: true },
    { key: "ops", label: "Opérations", icon: PackageSearch, show: has("shopship") },
    { key: "departures", label: "Départs", icon: CalendarClock, show: has("shopship") },
    { key: "users", label: "Clients", icon: Users, show: has("customers") },
    { key: "finance", label: "Finance", icon: CreditCard, show: has("reports") },
    { key: "geo", label: "Pays & villes", icon: Globe, show: has("geo") },
    { key: "hubs", label: "Hubs", icon: Building2, show: has("hubs") },
    { key: "catalog", label: "Catalogue & Tarifs", icon: Boxes, show: has("catalog") },
    { key: "accounts", label: "Utilisateurs", icon: UserRound, show: has("customers") },
    { key: "admins", label: "Admins", icon: UserCog, show: isSuper },
    { key: "audit", label: "Audit", icon: ScrollText, show: has("reports") },
    { key: "features", label: "Fonctionnalités", icon: Settings2, show: has("features") },
  ].filter((m) => m.show);
  const nextDeps = deps.filter((d) => d.active && !d.processed)
    .sort((a, b) => (a.departure_date || "").localeCompare(b.departure_date || "")).slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout items={MENU} active={tab} onSelect={setTab}
        header={
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-accent">{isSuper ? "Super Administrateur" : t("dash.admin")}</div>
              <h1 className="font-heading font-extrabold text-2xl tracking-tight">{MENU.find((m) => m.key === tab)?.label || "Administration"}</h1>
            </div>
            {/* PORTÉE OPÉRATIONNELLE : GLOBAL | HUB UK | HUB FRANCE + source */}
            <div className="flex flex-wrap items-center gap-2" data-testid="hub-scope-bar">
              <div className="flex border-2 border-primary">
                {[["all", "GLOBAL"], ["uk", "HUB UK · £"], ["fr", "HUB FRANCE · €"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`hub-scope-${v}`} onClick={() => setHub(v)}
                    className={`px-3 py-1.5 text-xs font-heading font-bold tracking-wide transition-colors ${hub === v ? "bg-primary text-white" : "bg-card hover:bg-secondary"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex border border-border">
                {[["all", "TOUT"], ["online", "ACHATS EN LIGNE"], ["goods", "MARCHANDISE"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`src-scope-${v}`} onClick={() => setSrc(v)}
                    className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase transition-colors ${src === v ? "bg-accent text-white" : "bg-card hover:bg-secondary"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        }>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsContent value="dashboard" className="mt-1">
            <h2 className="font-heading font-bold text-lg" data-testid="today-question">Qu&rsquo;est-ce qu&rsquo;on doit faire aujourd&rsquo;hui ?</h2>
            <div className="mt-3 grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              {cards.map((c, i) => (
                <button key={i} type="button" data-testid={`stat-card-${i}`} onClick={() => openCard(c)} disabled={!has("shopship")}
                  className={`text-left border p-4 transition-all enabled:cursor-pointer enabled:hover:border-accent enabled:active:scale-[0.98] disabled:opacity-60 ${c.value > 0 ? "border-border bg-card" : "border-border/60 bg-card opacity-70"}`}>
                  <c.icon className="h-5 w-5 text-accent" />
                  <div className="mt-1.5 font-heading font-extrabold text-3xl tracking-tight" data-testid={`stat-${i}`}>{c.value}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground leading-tight">{c.label}</div>
                  {has("shopship") && <ChevronHint />}
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg" data-testid="next-departures-title">Prochains départs</h2>
              {has("shopship") && <Button size="sm" variant="outline" className="rounded-sm" data-testid="see-all-departures" onClick={() => setTab("departures")}>Voir tout</Button>}
            </div>
            <div className="mt-2 border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Date</th><th className="px-3 py-2">Trajet</th><th className="px-3 py-2">Mode</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Clients / colis</th>
                  </tr>
                </thead>
                <tbody>
                  {nextDeps.map((d) => (
                    <tr key={d.id} data-testid={`dash-dep-${d.id}`} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50" onClick={() => setTab("departures")}>
                      <td className="px-3 py-2 font-bold whitespace-nowrap">{d.departure_date}</td>
                      <td className="px-3 py-2 font-semibold whitespace-nowrap">{d.origin_country} → {d.dest_country}</td>
                      <td className="px-3 py-2">{d.mode === "air" ? "Aérien" : "Maritime"}</td>
                      <td className="px-3 py-2"><Badge className="rounded-sm bg-blue-100 text-blue-700">Ouvert</Badge></td>
                      <td className="px-3 py-2">{d.clients_count || 0} client(s) · {d.shipments_count || 0} colis</td>
                    </tr>
                  ))}
                  {nextDeps.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Aucun départ planifié à venir.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {has("partners") && (
          <TabsContent value="partners" className="mt-5 overflow-x-auto">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {[["all", "Tous", partners.length],
                ["pending", "En attente", partners.filter((p) => p.status === "pending").length],
                ["approved", "Approuvés", partners.filter((p) => p.status === "approved").length],
                ["suspended", "Suspendus", partners.filter((p) => p.status === "suspended").length]].map(([v, l, n]) => (
                <button key={v} data-testid={`partnerfilter-${v}`} onClick={() => setFilter("partners", v)}
                  className={`px-3 py-1 text-xs font-semibold border transition-colors ${(filters.partners || "all") === v ? "bg-primary text-white border-primary" : "border-border hover:border-accent"}`}>
                  {l} ({n})
                </button>
              ))}
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Société</TableHead><TableHead>Localisation</TableHead><TableHead>Téléphone</TableHead><TableHead>Inscrit le</TableHead><TableHead>Docs</TableHead><TableHead>Note</TableHead>
                <TableHead>Routes</TableHead><TableHead>Niveau</TableHead><TableHead>Vérifié</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {partners.filter((p) => (filters.partners || "all") === "all" || (p.status || "approved") === filters.partners).map((p) => {
                  const [cls, label] = STATUS_BADGE[p.status || "approved"] || STATUS_BADGE.approved;
                  return (
                    <TableRow key={p.id} data-testid={`admin-partner-${p.id}`}>
                      <TableCell className="font-semibold">{p.company_name}<div className="text-xs text-muted-foreground">{p.email}</div></TableCell>
                      <TableCell>{p.city}, {p.country}</TableCell>
                      <TableCell className="text-xs">{p.phone || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(p.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{(p.documents || []).length || "—"}</TableCell>
                      <TableCell>{p.rating}/5</TableCell>
                      <TableCell>{p.routes_count}</TableCell>
                      <TableCell>
                        <Select value={p.level} onValueChange={(v) => setLevel(p, v)}>
                          <SelectTrigger className="h-8 w-32 rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="NEW">NEW</SelectItem><SelectItem value="CONFIRMED">CONFIRMED</SelectItem><SelectItem value="PREMIUM">PREMIUM</SelectItem></SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {p.verified
                          ? <Button size="sm" variant="outline" className="rounded-sm" data-testid={`unverify-${p.id}`} onClick={() => verify(p, false)}>{t("admin.unverify")}</Button>
                          : <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`verify-${p.id}`} onClick={() => verify(p, true)}>{t("admin.verify")}</Button>}
                      </TableCell>
                      <TableCell><Badge className={`rounded-sm ${cls}`} data-testid={`status-${p.id}`}>{label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" className="rounded-sm" data-testid={`capabilities-${p.id}`} onClick={() => setCapPartner(p)}>Capacités</Button>
                          {p.status !== "approved" && <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${p.id}`} onClick={() => setStatus(p, "approved")}>Approuver</Button>}
                          {p.status === "approved" && <Button size="sm" variant="outline" className="rounded-sm text-red-600" data-testid={`suspend-${p.id}`} onClick={() => setStatus(p, "suspended")}>Suspendre</Button>}
                          {p.status === "pending" && <Button size="sm" variant="outline" className="rounded-sm" data-testid={`refuse-${p.id}`} onClick={() => setStatus(p, "refused")}>Refuser</Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <CapabilitiesDialog partner={capPartner} open={!!capPartner} onOpenChange={(v) => { if (!v) setCapPartner(null); }} />
          </TabsContent>
          )}

          {has("shopship") && (
          <TabsContent value="ops" className="mt-5">
            <AdminOps filter={filters.ops} setFilter={(v) => setFilter("ops", v)} hub={hub} src={src} />
          </TabsContent>
          )}

          {has("shopship") && (
          <TabsContent value="departures" className="mt-5">
            <AdminDepartures hub={hub} />
          </TabsContent>
          )}

          {has("disputes") && (
          <TabsContent value="disputes" className="mt-5">
            <DisputesPanel disputes={disputes} filter={filters.disputes || "open"} setFilter={(f) => setFilter("disputes", f)}
              onResolve={async (d) => { await api.put(`/admin/disputes/${d.id}/resolve`); toast.success("Litige résolu"); load(); }} />
          </TabsContent>
          )}

          {has("reports") && (
          <TabsContent value="finance" className="mt-5">
            <AdminInvoices />
            <AdminFinance hub={hub} src={src} />
            <div className="mt-8 border-t pt-6">
              <h3 className="font-heading font-bold mb-3">Analytique</h3>
              <AdminAnalytics hub={hub} src={src} />
            </div>
          </TabsContent>
          )}

          {has("hubs") && (
          <TabsContent value="hubs" className="mt-5">
            <AdminHubs />
          </TabsContent>
          )}

          {has("catalog") && (
          <TabsContent value="catalog" className="mt-5">
            {/* Catalogue & Tarifs — le Catalogue existant + la section Tarifs reliée (Phase 5A-1) */}
            <Tabs defaultValue="articles">
              <TabsList className="rounded-sm">
                <TabsTrigger value="articles" data-testid="ct-tab-articles">Catalogue</TabsTrigger>
                <TabsTrigger value="tariffs" data-testid="ct-tab-tariffs">Tarifs</TabsTrigger>
              </TabsList>
              <TabsContent value="articles" className="mt-4"><AdminCatalog /></TabsContent>
              <TabsContent value="tariffs" className="mt-4"><AdminTariffs /></TabsContent>
            </Tabs>
          </TabsContent>
          )}

          {has("customers") && (
          <TabsContent value="users" className="mt-5">
            <ClientsPanel users={users.filter((u) => u.role === "customer")} shipments={shipments} purchases={ssPurchases} isSuper={isSuper}
              onChanged={() => api.get("/admin/users").then(({ data }) => setUsers(data))} />
          </TabsContent>
          )}

          {has("customers") && (
          <TabsContent value="accounts" className="mt-5">
            <AccountsPanel users={users} onChanged={() => api.get("/admin/users").then(({ data }) => setUsers(data))} />
          </TabsContent>
          )}

          {has("geo") && (
          <TabsContent value="geo" className="mt-5">
            <GeoPanel geo={geo} routes={routes} partners={partners}
              filter={filters.geo || "countries"} setFilter={(f) => setFilter("geo", f)}
              onUpdateCountry={updateCountry} onUpdateCity={updateCity} onChange={load} />
            <div className="mt-8 border-t pt-6">
              <GeoManager geo={geo} onUpdateCountry={updateCountry} onUpdateCity={updateCity} onChange={load} />
            </div>
          </TabsContent>
          )}

          {has("features") && (
          <TabsContent value="features" className="mt-5">
            <ProhibitedEditor />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl">
              {features && Object.keys(FEATURE_LABELS).map((key) => (
                <div key={key} className="border border-border bg-card p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{FEATURE_LABELS[key]}</div>
                    <div className="text-xs text-muted-foreground">{features[key] ? "Activé" : "Désactivé"}</div>
                  </div>
                  <Switch data-testid={`feature-${key}`} checked={!!features[key]} onCheckedChange={(v) => toggleFeature(key, v)} />
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground max-w-2xl">Une fonctionnalité désactivée disparaît du site public et son API est bloquée côté serveur. La désactivation ne supprime jamais de données.</p>
            <CommsSettings />
          </TabsContent>
          )}

          {isSuper && (
          <TabsContent value="admins" className="mt-5">
            <AdminsManager admins={admins} onChange={load} />
          </TabsContent>
          )}

          {has("reports") && (
          <TabsContent value="audit" className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Acteur</TableHead><TableHead>Action</TableHead><TableHead>Entité</TableHead><TableHead>Ancienne valeur</TableHead><TableHead>Nouvelle valeur</TableHead></TableRow></TableHeader>
              <TableBody>
                {audit.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-${a.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{a.actor_email}</TableCell>
                    <TableCell><Badge className="rounded-sm">{a.action}</Badge></TableCell>
                    <TableCell className="text-xs">{a.entity} {a.entity_id?.slice(0, 10)}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{a.old_value ? JSON.stringify(a.old_value) : "—"}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{a.new_value ? JSON.stringify(a.new_value) : "—"}</TableCell>
                  </TableRow>
                ))}
                {audit.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">Aucune action enregistrée.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TabsContent>
          )}
        </Tabs>
      </AdminLayout>
    </div>
  );
}

// UTILISATEURS : tous les comptes de la plateforme (clients, partenaires, admins) — activation/suspension
function AccountsPanel({ users, onChanged }) {
  const [q, setQ] = useState("");
  const ROLE = { customer: ["Client", "bg-secondary text-primary"], partner: ["Partenaire", "bg-blue-100 text-blue-700"],
    admin: ["Admin", "bg-amber-100 text-amber-700"], super_admin: ["Super Admin", "bg-accent text-white"] };
  const rows = users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase()));
  const setActive = async (u, active) => {
    try {
      await api.post(`/admin/users/${u.user_id}/status`, { active });
      toast.success(active ? "Compte réactivé" : "Compte suspendu");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div data-testid="accounts-panel">
      <Input data-testid="accounts-search" placeholder="Rechercher…" className="max-w-xs rounded-sm" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mt-3 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Téléphone / WhatsApp</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead><TableHead>Actif</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.slice(0, 200).map((u) => {
              const [lbl, cls] = ROLE[u.role] || [u.role, "bg-secondary"];
              return (
                <TableRow key={u.user_id} data-testid={`account-row-${u.user_id}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell className="text-xs">{u.whatsapp || u.phone || "—"}</TableCell>
                  <TableCell><Badge className={`rounded-sm ${cls}`}>{lbl}</Badge></TableCell>
                  <TableCell>{u.archived ? <Badge className="rounded-sm bg-muted text-muted-foreground">Archivé</Badge> : u.active === false ? <Badge className="rounded-sm bg-red-100 text-red-700">Suspendu</Badge> : <Badge className="rounded-sm bg-emerald-600 text-white">Actif</Badge>}</TableCell>
                  <TableCell>{u.role !== "super_admin" && <Switch data-testid={`account-active-${u.user_id}`} checked={u.active !== false} onCheckedChange={(v) => setActive(u, v)} />}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">Aucun compte.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GeoManager({ geo, onUpdateCountry, onUpdateCity, onChange }) {
  const { t } = useI18n();
  const [country, setCountry] = useState({ name: "", code: "", flag: "", origin_enabled: false, dest_enabled: false });
  const [city, setCity] = useState({ country_code: "", name: "" });
  const [selCountry, setSelCountry] = useState("");

  const addCountry = async () => {
    try { await api.post("/admin/countries", { ...country, active: true }); toast.success("Pays ajouté"); setCountry({ name: "", code: "", flag: "", origin_enabled: false, dest_enabled: false }); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const addCity = async () => {
    try { await api.post("/admin/cities", city); toast.success("Ville ajoutée"); setCity({ country_code: "", name: "" }); onChange(); }
    catch (e) { toast.error("Erreur"); }
  };
  const cities = geo.cities.filter((c) => !selCountry || c.country_code === selCountry);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-5">
          <h3 className="font-heading font-bold flex items-center gap-2"><Globe className="h-4 w-4 text-accent" /> Ajouter un pays</h3>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><Label>Nom</Label><Input className="rounded-sm" value={country.name} onChange={(e) => setCountry({ ...country, name: e.target.value })} data-testid="country-name" /></div>
            <div><Label>Code (ISO2)</Label><Input className="rounded-sm" value={country.code} onChange={(e) => setCountry({ ...country, code: e.target.value })} data-testid="country-code" /></div>
            <div><Label>Drapeau (emoji)</Label><Input className="rounded-sm" value={country.flag} onChange={(e) => setCountry({ ...country, flag: e.target.value })} /></div>
            <div className="flex items-end gap-4 pb-1">
              <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="country-origin" checked={country.origin_enabled} onCheckedChange={(v) => setCountry({ ...country, origin_enabled: v })} /> Origine</label>
              <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="country-dest" checked={country.dest_enabled} onCheckedChange={(v) => setCountry({ ...country, dest_enabled: v })} /> Destination</label>
            </div>
          </div>
          <Button className="mt-3 rounded-sm bg-accent text-white gap-1" data-testid="add-country-btn" onClick={addCountry}><Plus className="h-4 w-4" /> {t("common.add")}</Button>
        </div>
        <div className="border border-border bg-card p-5">
          <h3 className="font-heading font-bold flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" /> Ajouter une ville</h3>
          <div className="mt-3 space-y-3">
            <div><Label>Pays</Label>
              <Select value={city.country_code} onValueChange={(v) => setCity({ ...city, country_code: v })}>
                <SelectTrigger data-testid="city-country-code" className="rounded-sm"><SelectValue placeholder="Choisir un pays" /></SelectTrigger>
                <SelectContent>{geo.countries.map((c) => <SelectItem key={c.id} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nom de la ville</Label><Input className="rounded-sm" value={city.name} onChange={(e) => setCity({ ...city, name: e.target.value })} data-testid="city-name" /></div>
            <Button className="rounded-sm bg-accent text-white gap-1" data-testid="add-city-btn" onClick={addCity}><Plus className="h-4 w-4" /> {t("common.add")}</Button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <h3 className="font-heading font-bold mb-2">Pays ({geo.countries.length})</h3>
        <Table>
          <TableHeader><TableRow><TableHead>Pays</TableHead><TableHead>Code</TableHead><TableHead>Actif</TableHead><TableHead>Origine</TableHead><TableHead>Destination</TableHead></TableRow></TableHeader>
          <TableBody>
            {geo.countries.map((c) => (
              <TableRow key={c.id} data-testid={`country-row-${c.code}`}>
                <TableCell className="font-semibold">{c.flag} {c.name}</TableCell>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell><Switch data-testid={`country-active-${c.code}`} checked={!!c.active} onCheckedChange={(v) => onUpdateCountry(c, { active: v })} /></TableCell>
                <TableCell><Switch data-testid={`country-origin-${c.code}`} checked={!!c.origin_enabled} onCheckedChange={(v) => onUpdateCountry(c, { origin_enabled: v })} /></TableCell>
                <TableCell><Switch data-testid={`country-dest-${c.code}`} checked={!!c.dest_enabled} onCheckedChange={(v) => onUpdateCountry(c, { dest_enabled: v })} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="overflow-x-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold">Villes ({cities.length})</h3>
          <Select value={selCountry} onValueChange={setSelCountry}>
            <SelectTrigger data-testid="filter-cities-country" className="rounded-sm w-56 h-9"><SelectValue placeholder="Filtrer par pays" /></SelectTrigger>
            <SelectContent>{geo.countries.map((c) => <SelectItem key={c.id} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Ville</TableHead><TableHead>Pays</TableHead><TableHead>Active</TableHead><TableHead>Origine</TableHead><TableHead>Destination</TableHead></TableRow></TableHeader>
          <TableBody>
            {cities.map((c) => (
              <TableRow key={c.id} data-testid={`city-row-${c.id}`}>
                <TableCell className="font-semibold">{c.name}</TableCell>
                <TableCell className="font-mono text-xs">{c.country_code}</TableCell>
                <TableCell><Switch data-testid={`city-active-${c.id}`} checked={!!c.active} onCheckedChange={(v) => onUpdateCity(c, { active: v })} /></TableCell>
                <TableCell><Switch checked={!!c.origin_enabled} onCheckedChange={(v) => onUpdateCity(c, { origin_enabled: v })} /></TableCell>
                <TableCell><Switch checked={!!c.dest_enabled} onCheckedChange={(v) => onUpdateCity(c, { dest_enabled: v })} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AdminsManager({ admins, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", permissions: [] });
  const [editing, setEditing] = useState(null);

  const togglePerm = (obj, setObj, p) => {
    const perms = obj.permissions.includes(p) ? obj.permissions.filter((x) => x !== p) : [...obj.permissions, p];
    setObj({ ...obj, permissions: perms });
  };
  const create = async () => {
    try {
      await api.post("/admin/admins", f);
      toast.success("Administrateur créé"); setOpen(false);
      setF({ email: "", name: "", password: "", permissions: [] }); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleActive = async (a, val) => { await api.put(`/admin/admins/${a.user_id}`, { active: val }); toast.success(val ? "Compte activé" : "Compte désactivé"); onChange(); };
  const savePerms = async () => {
    await api.put(`/admin/admins/${editing.user_id}`, { permissions: editing.permissions });
    toast.success("Permissions mises à jour"); setEditing(null); onChange();
  };
  const resetPassword = async (a) => {
    const pw = window.prompt(`Nouveau mot de passe pour ${a.email} (8 caractères min.)`);
    if (!pw) return;
    try { await api.post(`/admin/admins/${a.user_id}/reset-password`, { password: pw }); toast.success("Mot de passe réinitialisé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mb-4 rounded-sm bg-accent text-white gap-1" data-testid="add-admin-btn"><Plus className="h-4 w-4" /> Ajouter un administrateur</Button></DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvel administrateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="admin-name" className="rounded-sm" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="admin-email" type="email" className="rounded-sm" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div><Label>Mot de passe (8 car. min.)</Label><Input data-testid="admin-password" type="text" className="rounded-sm" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
            <div>
              <Label>Permissions</Label>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {Object.keys(PERMISSION_LABELS).map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input data-testid={`perm-${p}`} type="checkbox" className="h-4 w-4 accent-[#FF6600]" checked={f.permissions.includes(p)} onChange={() => togglePerm(f, setF, p)} />
                    {PERMISSION_LABELS[p]}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-admin-btn" className="rounded-sm bg-accent text-white" onClick={create}>Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Permissions</TableHead><TableHead>Actif</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {admins.map((a) => (
              <TableRow key={a.user_id} data-testid={`admin-row-${a.user_id}`}>
                <TableCell className="font-semibold">{a.name}</TableCell>
                <TableCell>{a.email}</TableCell>
                <TableCell><Badge className={`rounded-sm ${a.role === "super_admin" ? "bg-accent text-white" : ""}`}>{a.role === "super_admin" ? "Super Admin" : "Admin"}</Badge></TableCell>
                <TableCell className="text-xs max-w-[220px]">{a.role === "super_admin" ? "Toutes" : (a.permissions || []).map((p) => PERMISSION_LABELS[p]).join(", ") || "Aucune"}</TableCell>
                <TableCell>{a.role !== "super_admin" && <Switch data-testid={`admin-active-${a.user_id}`} checked={a.active !== false} onCheckedChange={(v) => toggleActive(a, v)} />}</TableCell>
                <TableCell>
                  {a.role !== "super_admin" && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-sm" data-testid={`edit-perms-${a.user_id}`} onClick={() => setEditing({ user_id: a.user_id, email: a.email, permissions: a.permissions || [] })}>Permissions</Button>
                      <Button size="sm" variant="outline" className="rounded-sm" data-testid={`reset-pw-${a.user_id}`} onClick={() => resetPassword(a)}>Réinit. mot de passe</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Permissions — {editing?.email}</DialogTitle><DialogDescription>Cochez les sections accessibles à cet administrateur.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-1.5">
            {editing && Object.keys(PERMISSION_LABELS).map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid={`edit-perm-${p}`} type="checkbox" className="h-4 w-4 accent-[#FF6600]" checked={editing.permissions.includes(p)} onChange={() => togglePerm(editing, setEditing, p)} />
                {PERMISSION_LABELS[p]}
              </label>
            ))}
          </div>
          <DialogFooter><Button data-testid="save-perms-btn" className="rounded-sm bg-accent text-white" onClick={savePerms}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommsSettings() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/comms").then(({ data }) => setF(data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = async () => {
    const { data } = await api.put("/admin/comms", f);
    setF(data);
    toast.success("Paramètres de communication enregistrés");
  };
  return (
    <div className="mt-8 max-w-2xl border border-border bg-card p-5" data-testid="comms-settings">
      <h3 className="font-heading font-bold text-lg">Communication &amp; alertes</h3>
      <p className="mt-1 text-xs text-muted-foreground">WhatsApp = communication client · Email = alerte Admin · Colis2Go = traitement.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Numéro WhatsApp Colis2Go</Label>
          <Input data-testid="comms-whatsapp" className="rounded-sm" placeholder="+44 7466 398452"
            value={f.whatsapp_number} onChange={(e) => setF({ ...f, whatsapp_number: e.target.value })} />
        </div>
        <div>
          <Label>Email des alertes Admin</Label>
          <Input data-testid="comms-email" className="rounded-sm" placeholder="alertes@colis2go.com"
            value={f.admin_alert_email} onChange={(e) => setF({ ...f, admin_alert_email: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label>URL de l&rsquo;application (liens « Ouvrir le dossier » dans les emails)</Label>
          <Input data-testid="comms-appurl" className="rounded-sm" placeholder="https://colis2go.com"
            value={f.app_url} onChange={(e) => setF({ ...f, app_url: e.target.value })} />
        </div>
      </div>
      <Button data-testid="comms-save" className="mt-4 rounded-sm bg-accent text-white" onClick={save}>Enregistrer</Button>
    </div>
  );
}
